import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPanel = () => {
  const [showPanel, setShowPanel] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('messages');
  const [messages, setMessages] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [streamingData, setStreamingData] = useState({ tracks: [], summary: {} });
  const [landingPages, setLandingPages] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // Mostrar botón solo cuando esté cerca del final de la página (últimos 200px)
      const isNearBottom = scrollTop + windowHeight >= documentHeight - 200;
      setShowButton(isNearBottom);
    };

    window.addEventListener('scroll', handleScroll);
    // Verificar posición inicial
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    setLoginError('');

    // Credenciales de admin desde variables de entorno
    const ADMIN_USERNAME = process.env.REACT_APP_ADMIN_USERNAME;
    const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD;

    if (loginData.username === ADMIN_USERNAME && loginData.password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setShowLogin(false);
      setShowPanel(true);
      setLoginData({ username: '', password: '' });
    } else {
      setLoginError('Usuario o contraseña incorrectos');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setShowPanel(false);
    setLoginData({ username: '', password: '' });
  };

  const openAdmin = () => {
    if (isAuthenticated) {
      setShowPanel(true);
    } else {
      setShowLogin(true);
    }
  };

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/contact/messages`);
      const data = await response.json();
      setMessages(data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscribers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/newsletter/emails`);
      const data = await response.json();
      setSubscribers(data.subscribers || []);
      
      const statsResponse = await fetch(`${API}/newsletter/subscribers`);
      const statsData = await statsResponse.json();
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching subscribers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStreamingData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/tracks/streaming`);
      const data = await response.json();
      setStreamingData(data);
    } catch (error) {
      console.error('Error fetching streaming data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLandingPagesData = () => {
    // Static data for landing pages since they're frontend routes
    const baseUrl = window.location.origin;
    const landingPagesData = [
      // Spanish (Default)
      {
        id: 0,
        name: 'UPLIGHT',
        url: `${baseUrl}/uplight`,
        trackId: '0',
        status: 'active'
      },
      {
        id: 1,
        name: 'When I Wake',
        url: `${baseUrl}/when-i-wake`,
        trackId: '6',
        status: 'active'
      },
      {
        id: 2,
        name: 'Floating in Time',
        url: `${baseUrl}/floating-in-time`,
        trackId: '1',
        status: 'active'
      },
      {
        id: 3,
        name: 'Shades',
        url: `${baseUrl}/shades`,
        trackId: '2',
        status: 'active'
      },
      {
        id: 4,
        name: 'Insane',
        url: `${baseUrl}/insane`,
        trackId: '3',
        status: 'active'
      },
      {
        id: 5,
        name: 'Its Over',
        url: `${baseUrl}/its-over`,
        trackId: '4',
        status: 'active'
      },
      {
        id: 6,
        name: 'Odyssey',
        url: `${baseUrl}/odyssey`,
        trackId: '5',
        status: 'active'
      },
      
      // English Versions
      {
        id: 7,
        name: 'UPLIGHT (EN)',
        url: `${baseUrl}/uplight?lang=en`,
        trackId: '0',
        status: 'active'
      },
      {
        id: 8,
        name: 'When I Wake (EN)',
        url: `${baseUrl}/when-i-wake?lang=en`,
        trackId: '6',
        status: 'active'
      },
      {
        id: 9,
        name: 'Floating in Time (EN)',
        url: `${baseUrl}/floating-in-time?lang=en`,
        trackId: '1',
        status: 'active'
      },
      {
        id: 10,
        name: 'Shades (EN)',
        url: `${baseUrl}/shades?lang=en`,
        trackId: '2',
        status: 'active'
      },
      {
        id: 11,
        name: 'Insane (EN)',
        url: `${baseUrl}/insane?lang=en`,
        trackId: '3',
        status: 'active'
      },
      {
        id: 12,
        name: 'Its Over (EN)',
        url: `${baseUrl}/its-over?lang=en`,
        trackId: '4',
        status: 'active'
      },
      {
        id: 13,
        name: 'Odyssey (EN)',
        url: `${baseUrl}/odyssey?lang=en`,
        trackId: '5',
        status: 'active'
      },
      
      // Portuguese Versions
      {
        id: 14,
        name: 'UPLIGHT (PT)',
        url: `${baseUrl}/uplight?lang=pt`,
        trackId: '0',
        status: 'active'
      },
      {
        id: 15,
        name: 'When I Wake (PT)',
        url: `${baseUrl}/when-i-wake?lang=pt`,
        trackId: '6',
        status: 'active'
      },
      {
        id: 16,
        name: 'Floating in Time (PT)',
        url: `${baseUrl}/floating-in-time?lang=pt`,
        trackId: '1',
        status: 'active'
      },
      {
        id: 17,
        name: 'Shades (PT)',
        url: `${baseUrl}/shades?lang=pt`,
        trackId: '2',
        status: 'active'
      },
      {
        id: 18,
        name: 'Insane (PT)',
        url: `${baseUrl}/insane?lang=pt`,
        trackId: '3',
        status: 'active'
      },
      {
        id: 19,
        name: 'Its Over (PT)',
        url: `${baseUrl}/its-over?lang=pt`,
        trackId: '4',
        status: 'active'
      },
      {
        id: 20,
        name: 'Odyssey (PT)',
        url: `${baseUrl}/odyssey?lang=pt`,
        trackId: '5',
        status: 'active'
      },

      // Spotify Deeplink Campaign - Afternoon with Myself
      {
        id: 21,
        name: 'Afternoon with Myself - Spotify (ES)',
        url: `${baseUrl}/afternoon-with-myself?lang=es`,
        trackId: 'afternoon-2026',
        status: 'active'
      },
      {
        id: 22,
        name: 'Afternoon with Myself - Spotify (EN)',
        url: `${baseUrl}/afternoon-with-myself?lang=en`,
        trackId: 'afternoon-2026',
        status: 'active'
      },
      {
        id: 23,
        name: 'Afternoon with Myself - Spotify (PT)',
        url: `${baseUrl}/afternoon-with-myself?lang=pt`,
        trackId: 'afternoon-2026',
        status: 'active'
      },
      {
        id: 24,
        name: 'Afternoon with Myself - Spotify (FR)',
        url: `${baseUrl}/afternoon-with-myself?lang=fr`,
        trackId: 'afternoon-2026',
        status: 'active'
      },
      {
        id: 25,
        name: 'Afternoon with Myself - Spotify (DE)',
        url: `${baseUrl}/afternoon-with-myself?lang=de`,
        trackId: 'afternoon-2026',
        status: 'active'
      },
      {
        id: 26,
        name: 'Afternoon with Myself - Spotify (IT)',
        url: `${baseUrl}/afternoon-with-myself?lang=it`,
        trackId: 'afternoon-2026',
        status: 'active'
      },

      // Spotify Deeplink Campaigns - All Tracks (6 languages each)
      ...[
        { name: 'Take My Hand', slug: 'take-my-hand' },
        { name: 'Odyssey', slug: 'odyssey' },
        { name: 'Its Over', slug: 'its-over' },
        { name: 'Shades', slug: 'shades' },
        { name: 'Floating in Time', slug: 'floating-in-time' },
        { name: 'Floating in Time Instrumental', slug: 'floating-in-time-instrumental' },
        { name: 'When I Wake', slug: 'when-i-wake' },
        { name: 'Insane', slug: 'insane' },
        { name: 'UPLIGHT', slug: 'uplight' },
      ].flatMap((track, trackIdx) =>
        ['es', 'en', 'pt', 'fr', 'de', 'it'].map((lang, langIdx) => ({
          id: 27 + (trackIdx * 6) + langIdx,
          name: `${track.name} - Spotify (${lang.toUpperCase()})`,
          url: `${baseUrl}/listen/${track.slug}?lang=${lang}`,
          trackId: track.slug,
          status: 'active'
        }))
      ),

      // Open Graph Share URLs (for social media campaigns)
      ...[
        { name: 'Take My Hand', slug: 'take-my-hand' },
        { name: 'Afternoon with Myself', slug: 'afternoon-with-myself' },
        { name: 'Odyssey', slug: 'odyssey' },
        { name: 'Its Over', slug: 'its-over' },
        { name: 'Shades', slug: 'shades' },
        { name: 'Floating in Time', slug: 'floating-in-time' },
        { name: 'Floating in Time Instrumental', slug: 'floating-in-time-instrumental' },
        { name: 'When I Wake', slug: 'when-i-wake' },
        { name: 'Insane', slug: 'insane' },
        { name: 'UPLIGHT', slug: 'uplight' },
      ].flatMap((track, trackIdx) =>
        ['es', 'en', 'pt', 'fr', 'de', 'it'].map((lang, langIdx) => ({
          id: 75 + (trackIdx * 6) + langIdx,
          name: `OG Share: ${track.name} (${lang.toUpperCase()})`,
          url: `${baseUrl}/api/share/${track.slug}?lang=${lang}`,
          trackId: track.slug,
          status: 'active'
        }))
      ),

      // Playlist Campaigns
      ...[
        { name: "80's Synthpop Odyssey", slug: '80s-synthpop-odyssey' },
      ].flatMap((pl, plIdx) =>
        ['es', 'en', 'pt', 'fr', 'de', 'it'].map((lang, langIdx) => ({
          id: 130 + (plIdx * 6) + langIdx,
          name: `Playlist: ${pl.name} (${lang.toUpperCase()})`,
          url: `${baseUrl}/api/share/playlist/${pl.slug}?lang=${lang}`,
          trackId: pl.slug,
          status: 'active'
        }))
      )
    ];
    setLandingPages(landingPagesData);
  };

  useEffect(() => {
    if (showPanel && activeTab === 'messages') {
      fetchMessages();
    } else if (showPanel && activeTab === 'subscribers') {
      fetchSubscribers();
    } else if (showPanel && activeTab === 'streaming') {
      fetchStreamingData();
    } else if (showPanel && activeTab === 'landingPages') {
      fetchLandingPagesData();
    }
  }, [showPanel, activeTab]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'collaboration': return 'text-blue-400';
      case 'sync': return 'text-green-400';
      case 'general': return 'text-gray-400';
      default: return 'text-white';
    }
  };

  return (
    <>
      {/* Botón Admin - Solo visible cuando está cerca del final */}
      {showButton && !showPanel && !showLogin && (
        <div className="fixed bottom-4 right-4 z-50">
          <Button
            onClick={openAdmin}
            className="bg-black/80 border border-white/20 text-white hover:bg-white hover:text-black transition-all duration-300 backdrop-blur-sm text-xs px-3 py-1"
          >
            ADMIN
          </Button>
        </div>
      )}

      {/* Pantalla de Login */}
      {showLogin && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="bg-black/95 border-white/20 w-full max-w-md">
            <div className="p-6">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-light text-white tracking-wider mb-2">SYNTETIK ADMIN</h2>
                <p className="text-gray-400 text-sm">Acceso restringido</p>
              </div>

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-white text-sm font-light mb-2">
                    Usuario
                  </label>
                  <input
                    type="text"
                    value={loginData.username}
                    onChange={(e) => setLoginData({...loginData, username: e.target.value})}
                    className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none"
                    placeholder="syntetik"
                    required
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-light mb-2">
                    Contraseña
                  </label>
                  <input
                    type="password"
                    value={loginData.password}
                    onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                    className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none"
                    placeholder="••••••••"
                    required
                  />
                </div>

                {loginError && (
                  <div className="text-red-400 text-sm text-center">
                    {loginError}
                  </div>
                )}

                <div className="flex space-x-3 pt-4">
                  <Button
                    type="submit"
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white border-none"
                  >
                    ACCEDER
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setShowLogin(false)}
                    className="flex-1 bg-transparent border border-white/30 text-white hover:bg-white hover:text-black"
                  >
                    CANCELAR
                  </Button>
                </div>
              </form>

              <div className="mt-6 pt-4 border-t border-white/10">
                <p className="text-gray-400 text-xs text-center">
                  Solo personal autorizado
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Panel Admin */}
      {showPanel && isAuthenticated && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-end justify-center p-4">
          <Card className="bg-black/95 border-white/20 w-full max-w-6xl max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-white/10">
              <h2 className="text-2xl font-light text-white tracking-wider">SYNTETIK ADMIN</h2>
              <div className="flex space-x-3">
                <Button
                  onClick={handleLogout}
                  className="bg-red-600/20 border border-red-400/30 text-red-300 hover:bg-red-600 hover:text-white transition-all duration-300 text-sm px-4 py-2"
                >
                  LOGOUT
                </Button>
                <Button
                  onClick={() => setShowPanel(false)}
                  className="bg-transparent border border-white/30 text-white hover:bg-white hover:text-black transition-all duration-300 text-sm px-4 py-2"
                >
                  CLOSE
                </Button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-white/10">
              <Button
                onClick={() => setActiveTab('messages')}
                className={`flex-1 py-4 px-6 rounded-none ${
                  activeTab === 'messages' 
                    ? 'bg-white/10 text-white border-b-2 border-blue-400' 
                    : 'bg-transparent text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                MENSAJES ({messages.length})
              </Button>
              <Button
                onClick={() => setActiveTab('subscribers')}
                className={`flex-1 py-4 px-6 rounded-none ${
                  activeTab === 'subscribers' 
                    ? 'bg-white/10 text-white border-b-2 border-blue-400' 
                    : 'bg-transparent text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                SUSCRIPTORES ({subscribers.length})
              </Button>
              <Button
                onClick={() => setActiveTab('streaming')}
                className={`flex-1 py-4 px-6 rounded-none ${
                  activeTab === 'streaming' 
                    ? 'bg-white/10 text-white border-b-2 border-blue-400' 
                    : 'bg-transparent text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                STREAMING ({streamingData.tracks.length})
              </Button>
              
              <Button
                onClick={() => setActiveTab('landingPages')}
                className={`flex-1 py-4 px-6 rounded-none ${
                  activeTab === 'landingPages' 
                    ? 'bg-white/10 text-white border-b-2 border-blue-400' 
                    : 'bg-transparent text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                LANDING PAGES ({landingPages.length})
              </Button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {loading ? (
                <div className="text-center text-white py-8">
                  <div className="animate-spin h-8 w-8 border-2 border-white border-t-transparent rounded-full mx-auto mb-4"></div>
                  Loading...
                </div>
              ) : activeTab === 'messages' ? (
                <div className="space-y-4">
                  <div className="text-white mb-6">
                    <p className="text-lg">📧 Total Messages: <span className="text-blue-400">{messages.length}</span></p>
                  </div>
                  
                  {messages.length === 0 ? (
                    <p className="text-gray-400 text-center py-8">No messages yet.</p>
                  ) : (
                    messages.map((message) => (
                      <Card key={message.id} className="bg-white/5 border-white/10 p-4 hover:bg-white/10 transition-colors">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="text-white font-medium">{message.name}</h4>
                            <p className="text-blue-400 text-sm">{message.email}</p>
                            {message.subject && (
                              <p className="text-gray-300 text-sm mt-1">"{message.subject}"</p>
                            )}
                          </div>
                          <div className="text-right">
                            <span className={`text-xs px-2 py-1 rounded-full bg-white/10 ${getTypeColor(message.inquiry_type)}`}>
                              {message.inquiry_type?.toUpperCase()}
                            </span>
                            <p className="text-gray-400 text-xs mt-1">{formatDate(message.created_at)}</p>
                          </div>
                        </div>
                        <p className="text-gray-300 text-sm leading-relaxed">{message.message}</p>
                      </Card>
                    ))
                  )}
                </div>
              ) : activeTab === 'subscribers' ? (
                <div className="space-y-4">
                  <div className="text-white mb-6 grid grid-cols-2 gap-4">
                    <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                      <p className="text-2xl font-light text-blue-400">{stats.total_subscribers || subscribers.length}</p>
                      <p className="text-sm text-gray-400">Total Subscribers</p>
                    </div>
                    <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                      <p className="text-2xl font-light text-green-400">{stats.recent_subscribers || 0}</p>
                      <p className="text-sm text-gray-400">This Month</p>
                    </div>
                  </div>

                  {subscribers.length === 0 ? (
                    <p className="text-gray-400 text-center py-8">No subscribers yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {subscribers.map((subscriber, index) => (
                        <Card key={index} className="bg-white/5 border-white/10 p-3 hover:bg-white/10 transition-colors">
                          <div className="flex justify-between items-center">
                            <span className="text-blue-400 font-medium">{subscriber.email}</span>
                            <span className="text-gray-400 text-xs">{formatDate(subscriber.subscribed_at)}</span>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              ) : activeTab === 'streaming' ? (
                <div className="space-y-4">
                  {/* Streaming Summary */}
                  <div className="text-white mb-6 grid grid-cols-3 gap-4">
                    <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                      <p className="text-2xl font-light text-blue-400">{streamingData.summary.total_tracks || 0}</p>
                      <p className="text-sm text-gray-400">Total Tracks</p>
                    </div>
                    <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                      <p className="text-2xl font-light text-green-400">{streamingData.summary.total_plays || 0}</p>
                      <p className="text-sm text-gray-400">Total Plays</p>
                    </div>
                    <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                      <p className="text-2xl font-light text-purple-400">{streamingData.summary.average_plays || 0}</p>
                      <p className="text-sm text-gray-400">Avg per Track</p>
                    </div>
                  </div>

                  {/* Tracks List */}
                  {streamingData.tracks.length === 0 ? (
                    <p className="text-gray-400 text-center py-8">No streaming data yet.</p>
                  ) : (
                    <div className="space-y-3">
                      <h3 className="text-white text-lg mb-4 border-b border-white/10 pb-2">🎵 Tracks por Streaming</h3>
                      {streamingData.tracks.map((track, index) => (
                        <Card key={track.id} className="bg-white/5 border-white/10 p-4 hover:bg-white/10 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <div className="bg-blue-500/20 rounded-full w-8 h-8 flex items-center justify-center">
                                <span className="text-blue-300 text-sm font-medium">#{index + 1}</span>
                              </div>
                              <div>
                                <h4 className="text-white font-medium">{track.title}</h4>
                                <p className="text-gray-400 text-sm">
                                  {track.album} • {track.year} • {track.duration}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-6 text-right">
                              <div>
                                <p className="text-green-400 text-lg font-medium">{track.total_plays.toLocaleString()}</p>
                                <p className="text-gray-400 text-xs">Total Plays</p>
                              </div>
                              <div className="w-20 h-2 bg-gray-700 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-gradient-to-r from-green-400 to-blue-500 transition-all duration-500"
                                  style={{
                                    width: `${Math.min(100, (track.total_plays / Math.max(...streamingData.tracks.map(t => t.total_plays))) * 100)}%`
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              ) : activeTab === 'landingPages' ? (
                <div className="space-y-4">
                  {/* Landing Pages Summary */}
                  <div className="text-white mb-6 grid grid-cols-3 gap-4">
                    <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                      <p className="text-2xl font-light text-green-400">{landingPages.length}</p>
                      <p className="text-sm text-gray-400">Total Pages</p>
                    </div>
                    <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                      <p className="text-2xl font-light text-blue-400">{landingPages.filter(p => p.status === 'active').length}</p>
                      <p className="text-sm text-gray-400">Active</p>
                    </div>
                    <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                      <p className="text-2xl font-light text-purple-400">6</p>
                      <p className="text-sm text-gray-400">Tracks</p>
                    </div>
                  </div>

                  {/* Landing Pages List */}
                  <div className="space-y-3">
                    <h3 className="text-white text-lg mb-4 border-b border-white/10 pb-2">🚀 Landing Pages SYNTETIK</h3>
                    {landingPages.map((page, index) => (
                      <Card key={page.id} className="bg-white/5 border-white/10 p-4 hover:bg-white/10 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="bg-green-500/20 rounded-full w-8 h-8 flex items-center justify-center">
                              <span className="text-green-300 text-sm font-medium">#{index + 1}</span>
                            </div>
                            <div>
                              <h4 className="text-white font-medium">{page.name}</h4>
                              <p className="text-gray-400 text-sm">Track ID: {page.trackId} • Status: Active</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-4">
                            <div className="text-right">
                              <p className="text-blue-400 text-sm font-mono break-all max-w-80">{page.url}</p>
                              <p className="text-gray-400 text-xs">Landing Page URL</p>
                            </div>
                            <button 
                              onClick={() => navigator.clipboard.writeText(page.url)}
                              className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 px-3 py-1 rounded text-xs transition-colors"
                            >
                              Copy
                            </button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center text-gray-400 py-8">
                  Select a tab to view content
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </>
  );
};

export default AdminPanel;